import clsx from 'clsx'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useModal } from '@ebay/nice-modal-react'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import {
  Button,
  Tag,
  Avatar,
  Checkbox,
  Tooltip,
  AcceptSubmissionModal,
} from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'

import { useGetWorkflowOnOffState } from 'utils/hooks/workflows/useGetWorkflowToggledState'
import AssigneeDropdown from 'components/workflows/AssigneeDropdown'
import StatusDropdown from 'components/workflows/StatusDropdown'
import BulkReworkModal from 'container/Projects/LiveProjects/ProjectDetails/AssignmentTab/BulkReworkModal'

import StatusTag from '../StatusTag/StatusTag'
import DeleteItemModal from '../DeleteModal/DeleteItemModal'
import QuickActions from '../QuickActions/QuickActions'
import s from './AssignmentRow.module.css'

type Props = {
  companyId: string
  item: Assignment
  selectedItems: Assignment[]
  selectedItemsStage: AssignmentStage | null | ''
  onClick: (id: string, title?: string) => void
  setSelectedItems: Dispatch<SetStateAction<Assignment[]>>
  onDownload: (items: Assignment[]) => void
  refetchItems: () => void
  recentItems?: string[]
  assigneeList?: TeamMembers[]
  workflowStatusList?: WorkflowStatus[]
}

const AssignmentRow = ({
  item,
  selectedItems,
  selectedItemsStage,
  companyId,
  onClick,
  setSelectedItems,
  onDownload,
  refetchItems,
  recentItems,
  assigneeList,
  workflowStatusList,
}: Props) => {
  const [recentHighlight, setRecentHighlight] = useState(
    recentItems?.includes(item.id)
  )
  const { data: toggleState } = useGetWorkflowOnOffState({ companyId })
  const { show: showDeleteModal } = useModal(DeleteItemModal)
  const { show: showAcceptItemModal } = useModal(AcceptSubmissionModal)
  const { show: showBulkReworkModal } = useModal(BulkReworkModal)
  const isPrivateDoc = item.item_type === 'private_doc'
  const avatarURL = isPrivateDoc
    ? item?.added_by?.avatar
    : item?.creator_allotted?.avatar
  const assigneeFirstname = isPrivateDoc
    ? item?.added_by?.first_name
    : item?.creator_allotted?.first_name
  const assigneeLastname = isPrivateDoc
    ? item?.added_by?.last_name
    : item?.creator_allotted?.last_name
  const assigneeName = `${assigneeFirstname ?? ''} ${assigneeLastname ?? ''}`
  const docLink =
    item?.submission_file?.src_type === 'pepper_editor'
      ? `/${companyId}/document/${item.id}`
      : item?.submission_file?.file_url
  const isAllowedOpenDocs =
    (item.status === 'CLI_AWA' || item.status === 'CLI_FIN') && !!docLink

  const initials = `${assigneeFirstname?.[0]?.toUpperCase() ?? ''}${
    assigneeLastname?.[0]?.toUpperCase() ?? ''
  }`

  const handleClick = () => {
    if (isPrivateDoc) {
      window.open(`/${companyId}/items/${item.id}`, '_blank')
    } else {
      onClick(item.id, item.assignment_title)
    }
  }

  const handleDownload = () => {
    onDownload([item])
  }

  const handleOpenDoc = () => {
    window.open(docLink, '_blank')
  }

  const handleDelete = () => {
    showDeleteModal({
      items: [item],
      onSuccess: () => {
        refetchItems()
        setSelectedItems(prevItems =>
          prevItems.filter(_item => _item.id !== item.id)
        )
      },
    })
  }

  const handleRework = () => {
    showBulkReworkModal({
      assignments: [item],
      refetch: refetchItems,
    })
  }

  const handleApprove = () => {
    showAcceptItemModal({
      assignmentIds: [item.id],
      shouldSubmitAssignmentApproval: true,
      companyId,
      refetch: refetchItems,
    })
  }

  const handleSelect = (event: CheckboxChangeEvent) => {
    event.stopPropagation()
    const { checked } = event.target
    if (checked) {
      setSelectedItems([...selectedItems, item])
    } else {
      setSelectedItems(selectedItems.filter(_item => _item.id !== item.id))
    }
  }

  const isChecked = () => {
    return selectedItems.findIndex(_item => _item.id === item.id) !== -1
  }

  useEffect(() => {
    const HIGHLIGHT_TIME = 10_000
    setTimeout(() => setRecentHighlight(false), HIGHLIGHT_TIME)
  }, [])

  const workflowStatus = workflowStatusList?.find(
    status => status.id === item.workflow_status_id
  )
  const assigneeData = assigneeList?.find(
    assignee => assignee.id === item.workflow_assignee_id
  )

  return (
    <div
      className={clsx(
        s.row,
        {
          [s.grouped]: item.grouped,
        },
        recentHighlight ? s.backgroundActive : null
      )}
    >
      <div className={s.left}>
        <div role="none" onClick={event => event.stopPropagation()}>
          {typeof selectedItemsStage === 'string' &&
          selectedItemsStage !== item.stage ? (
            <Tooltip
              title="You can only select assignments with similar statuses"
              overlayClassName={s.tooltip}
              placement="bottom"
            >
              <Checkbox disabled={true} />
            </Tooltip>
          ) : (
            <Checkbox checked={isChecked()} onChange={handleSelect} />
          )}
        </div>
        <p className={s.titleWrapper}>
          <div className={s.codeTitle} role="none" onClick={handleClick}>
            <div className={s.code}>#{item.assignment_code}</div>
            <div className={s.title}>{item.assignment_title}</div>
            {item?.vertical?.name && (
              <div className={s.vertical}>
                <Tag className={s.tag}>{item?.vertical?.name}</Tag>
              </div>
            )}
          </div>
          {!isPrivateDoc && toggleState?.data.workflow_enabled === 1 && (
            <div className={s.workflowInfo}>
              <AssigneeDropdown
                companyId={companyId}
                assigneeList={assigneeList}
                itemId={item.id}
                assigneeId={item.workflow_assignee_id}
                isPrivateDoc={isPrivateDoc}
                showTooltip={true}
              >
                <div
                  className={clsx(s.pvtDocAssignee, {
                    [s.avatarBorder]: !assigneeData?.full_name,
                  })}
                  role="none"
                >
                  {!assigneeData?.full_name ? (
                    <Icon icon="lucide:user" height={16} />
                  ) : !assigneeData?.avatar ? (
                    <Avatar className={s.avatar}>
                      {assigneeData?.full_name[0]}
                    </Avatar>
                  ) : (
                    <Avatar src={assigneeData?.avatar} />
                  )}
                </div>
              </AssigneeDropdown>
              <div className={s.statusContainer}>
                <StatusDropdown
                  companyId={companyId}
                  statusList={workflowStatusList}
                  itemStatusId={item.workflow_status_id}
                  itemId={item.id}
                  isPrivateDoc={isPrivateDoc}
                >
                  <div className={s.itemStatus}>
                    <div
                      className={clsx(s.statusColor, {
                        [s.noColor]: workflowStatus?.name === 'No Status',
                      })}
                      style={{
                        backgroundColor: `#${workflowStatus?.color}`,
                        outline: `3px solid #${workflowStatus?.color}25`,
                      }}
                    />
                    <div className={s.statusText}>{workflowStatus?.name}</div>
                    <Icon
                      icon="icon-park-solid:down-one"
                      className={s.dropdownIcon}
                    />
                  </div>
                </StatusDropdown>
              </div>
            </div>
          )}
        </p>
        <div
          className={s.quickActionWrapper}
          role="none"
          onClick={event => event.stopPropagation()}
        >
          {!isPrivateDoc &&
            (isAllowedOpenDocs ? (
              <Button
                size="small"
                className={s.actionBtn}
                onClick={handleOpenDoc}
                icon={
                  <Icon icon="uil:external-link-alt" width={16} height={16} />
                }
                iconPlacement="right"
              >
                Open Doc
              </Button>
            ) : (
              !!docLink && (
                <Tooltip
                  title="Doc will be available on submission"
                  className={s.actionBtn}
                >
                  <span>
                    <Button
                      size="small"
                      className={s.actionBtn}
                      disabled={true}
                      icon={
                        <Icon
                          icon="uil:external-link-alt"
                          width={16}
                          height={16}
                        />
                      }
                      iconPlacement="right"
                    >
                      Open Doc
                    </Button>
                  </span>
                </Tooltip>
              )
            ))}
        </div>
      </div>

      <div className={s.right}>
        {item.client_rework_count > 0 && (
          <Tooltip
            title={`This assignment has gone through ${pluralize(
              item?.client_rework_count,
              'rework',
              's'
            )}.`}
          >
            <Tag className={s.tag}>
              <Icon icon="tabler:refresh" width={16} height={16} />
              {item.client_rework_count}
            </Tag>
          </Tooltip>
        )}
        <StatusTag
          stage={item.stage}
          client_approval_deadline={item?.client_approval_deadline}
        >
          <Tooltip title={assigneeName.trim()}>
            <div>
              <Avatar src={avatarURL} size={24}>
                {initials}
              </Avatar>
            </div>
          </Tooltip>
        </StatusTag>

        <div
          className={s.actionsWrapper}
          role="none"
          onClick={event => event.stopPropagation()}
        >
          <QuickActions
            item={item}
            onDownload={handleDownload}
            onApprove={handleApprove}
            onRework={handleRework}
            onDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  )
}

export default AssignmentRow
